import React, { useMemo, FC } from "react";

import { MdsTabBarItemContent } from "@/design-system/components/tab-bar/MdsTabBarItemContent";
import { MdsTabBarSection } from "@/design-system/components/tab-bar/types";
import { MDSText, MDSTextColor, MDSTextVariant, MDSTextWeight } from "@/mds";
import styled from "@emotion/styled";

export interface MdsTabBarSectionContentProps {
  isFirst: boolean;
  selectedItemId: string;
  section: MdsTabBarSection;
}

const Wrapper = styled.div({
  display: "flex",
  flexDirection: "column",
  flexWrap: "nowrap",
  justifyContent: "flex-start",
  alignItems: "flex-start",
  width: "100%",
});

const StyledSectionLabel = styled(MDSText)<{ $isFirst: boolean }>(({ theme, $isFirst }) => ({
  display: "flex",
  flexDirection: "row",
  flexWrap: "nowrap",
  justifyContent: "flex-start",
  alignItems: "center",
  padding: `${$isFirst ? theme.spacing[2] : theme.spacing[3]} ${theme.spacing[2]} ${theme.spacing[2]} ${theme.spacing[5]}`,
  userSelect: "none",
  "@container mds-panel (max-width: 750px)": {
    display: `none`,
  },
}));

const ItemContentWrapper = styled.div({
  display: "flex",
  flexDirection: "column",
  flexWrap: "nowrap",
  justifyContent: "flex-start",
  alignItems: "flex-start",
  width: "100%",
});

/**
 * @deprecated - Use the newer MDS components instead.
 *
 * - If something doesn't exist yet, please create a new MDS component and
 *   have it reviewed by the team!
 *
 * - If something needs to hook into our `stores`, add it to the /components directory.
 *
 * - If something is a one-off; please colocate it with the component/page that
 *   needs it.
 *
 * - If you have questions, please ask in the #hub-eng-web-team Slack channel.
 */
export const MdsTabBarSectionContent: FC<MdsTabBarSectionContentProps> = ({
  isFirst,
  selectedItemId,
  section,
}) => {
  const itemContent = useMemo(
    () =>
      section.items.map(item => (
        <MdsTabBarItemContent key={item.id} selectedItemId={selectedItemId} item={item} />
      )),
    [section.items, selectedItemId]
  );

  return (
    <Wrapper>
      <StyledSectionLabel
        $isFirst={isFirst}
        variant={MDSTextVariant.TextXs}
        weight={MDSTextWeight.SemiBold}
        color={MDSTextColor.Secondary}
      >
        {section.label}
      </StyledSectionLabel>
      <ItemContentWrapper>{itemContent}</ItemContentWrapper>
    </Wrapper>
  );
};
